import { ShippingDocumentKind } from "generated-graphql/graphql";
import { useState } from "react";

import { BillOfLadingReviewDialog } from "./BillOfLadingReviewDialog";
import { BillOfLadingUploadDialog } from "./BillOfLadingUploadDialog";
import { ManifestReviewDialog } from "./ManifestReviewDialog";
import { ManifestUploadDialog } from "./ManifestUploadDialog";
import { useReviewQueueRoute } from "./useReviewQueueRoute";

/** The kinds that have an upload flow; `Other` has none. */
export type UploadableShippingKind =
  | ShippingDocumentKind.Manifest
  | ShippingDocumentKind.BillOfLading;

type FlowProps = {
  tenantId: string;
  facilityId?: string;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

function ManifestUploadFlow({ tenantId, facilityId, open, onClose, onSaved }: FlowProps) {
  // The batch lives in the URL, not in state here: extraction takes long enough
  // that a reviewer wanders off, and a reloaded tab must still find the queue.
  const reviewQueue = useReviewQueueRoute();

  return (
    <>
      {open && (
        <ManifestUploadDialog
          open
          onClose={onClose}
          tenantId={tenantId}
          facilityId={facilityId}
          onExtractionReady={(documentIds) => reviewQueue.open(documentIds)}
        />
      )}

      {reviewQueue.currentDocumentId != null && (
        <ManifestReviewDialog
          onClose={reviewQueue.close}
          onComplete={onSaved}
          documentIds={reviewQueue.documentIds}
          currentDocumentId={reviewQueue.currentDocumentId}
          onNavigate={reviewQueue.navigate}
        />
      )}
    </>
  );
}

type ReviewTarget = { documentIds: string[]; facilityId: string; vendorSource?: string };

function BillOfLadingUploadFlow({ tenantId, facilityId, open, onClose, onSaved }: FlowProps) {
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);

  return (
    <>
      {open && (
        <BillOfLadingUploadDialog
          open
          onClose={onClose}
          tenantId={tenantId}
          facilityId={facilityId}
          onExtractionReady={(documentIds, appliedFacilityId, vendorSource) =>
            setReviewTarget({ documentIds, facilityId: appliedFacilityId, vendorSource })
          }
        />
      )}

      {reviewTarget && (
        <BillOfLadingReviewDialog
          documentIds={reviewTarget.documentIds}
          facilityId={reviewTarget.facilityId}
          initialVendorSource={reviewTarget.vendorSource}
          onClose={() => setReviewTarget(null)}
          onSaved={() => {
            setReviewTarget(null);
            onSaved?.();
          }}
        />
      )}
    </>
  );
}

/**
 * The upload and review dialogs for one kind of shipping record, never a
 * button. The host owns the trigger and decides whether the kind is available
 * to this user, so no feature flag is read here.
 */
export function ShippingRecordUploadFlow({
  documentKind,
  ...flow
}: FlowProps & { documentKind: UploadableShippingKind }) {
  return documentKind === ShippingDocumentKind.BillOfLading ? (
    <BillOfLadingUploadFlow {...flow} />
  ) : (
    <ManifestUploadFlow {...flow} />
  );
}
